import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Hexagon, LogOut, Loader2, Lock, Check, Zap, Shield, AlertTriangle,
  Swords, Sparkles, Target, Flame, RefreshCw, ChevronRight, Dumbbell, MonitorPlay,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const STAT_META = {
  strength: { label: "STR", full: "Strength" },
  dexterity: { label: "DEX", full: "Dexterity" },
  constitution: { label: "CON", full: "Constitution" },
  intelligence: { label: "INT", full: "Intelligence" },
  wisdom: { label: "WIS", full: "Wisdom" },
  charisma: { label: "CHA", full: "Charisma" },
};
const STAT_ORDER = ["strength", "dexterity", "constitution", "intelligence", "wisdom", "charisma"];

export default function System() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [flash, setFlash] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/system/profile");
      if (!data.unlocked) { navigate("/awaken"); return; }
      setData(data);
    } catch { navigate("/auth"); } finally { setLoadingData(false); }
  }, [navigate]);

  useEffect(() => { if (!loading && !user) navigate("/auth"); }, [loading, user, navigate]);
  useEffect(() => { if (user) load(); }, [user, load]);

  const showFlash = (t) => { setFlash(t); setTimeout(() => setFlash(""), 2200); };

  const logTask = async (idx) => {
    try {
      const { data: res } = await api.post("/system/log-task", { task_index: idx });
      if (res.already) return;
      setData((d) => ({ ...d, today_done: [...d.today_done, idx], progress: res.progress }));
      showFlash(`+${res.gained} XP`);
    } catch { /* */ }
  };

  const unlockNode = async (tier, node) => {
    try {
      const { data: res } = await api.post("/system/unlock-node", { tier, node });
      setData((d) => ({ ...d, unlocked_nodes: res.unlocked_nodes, progress: res.progress }));
      showFlash("Node unlocked");
    } catch (e) { showFlash(e.response?.data?.detail || "Cannot unlock"); }
  };

  if (loadingData || !data) {
    return <div className="h-screen grid-bg flex items-center justify-center"><Loader2 className="animate-spin text-cyan-400" size={32} /></div>;
  }

  const c = data.character;
  const p = data.progress;
  const rankSpan = p.rank_next ? p.rank_next - p.rank_floor : 200;
  const rankPct = Math.min(100, Math.round(((p.xp - p.rank_floor) / rankSpan) * 100));
  const nodeUnlocked = (ti, ni, base) =>
    base === "unlocked" || data.unlocked_nodes.some((u) => u.tier === ti && u.node === ni);

  return (
    <div className="min-h-screen bg-[#050505] grid-bg">
      <div className="fixed inset-0 glow-radial pointer-events-none" />
      {flash && (
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          className="fixed top-5 left-1/2 -translate-x-1/2 z-50 bg-cyan-400 text-black font-bold font-mono px-5 py-2 rounded-full shadow-[0_0_30px_rgba(0,240,255,0.6)]" data-testid="xp-flash">
          {flash}
        </motion.div>
      )}

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between px-5 sm:px-8 py-4 border-b border-blue-900/30 backdrop-blur">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
          <Hexagon className="text-cyan-400" size={20} />
          <span className="font-display font-bold tracking-[0.3em]">ARCHON</span>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="training-btn" onClick={() => navigate("/training")} className="mono-label flex items-center gap-1.5 border border-cyan-400/40 text-cyan-300 px-3 py-1.5 rounded-full hover:bg-cyan-400/10 transition-all">
            <Dumbbell size={13} /> Training
          </button>
          <button data-testid="coach-btn" onClick={() => navigate("/coach")} className="mono-label flex items-center gap-1.5 border border-cyan-400/40 text-cyan-300 px-3 py-1.5 rounded-full hover:bg-cyan-400/10 transition-all">
            <MonitorPlay size={13} /> Live Coach
          </button>
          <button data-testid="channel-btn" onClick={() => navigate("/channel")} className="mono-label border border-blue-900/40 px-3 py-1.5 rounded-full hover:bg-blue-500/10">Free Channel</button>
          <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }} className="text-slate-500 hover:text-red-400"><LogOut size={18} /></button>
        </div>
      </header>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Identity + rank */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6 sm:p-8" data-testid="identity-card">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="shrink-0 w-24 h-24 rounded-2xl grid place-items-center bg-gradient-to-br from-cyan-400/20 to-blue-700/20 border border-cyan-400/40">
              <span className="font-display text-5xl font-extrabold text-cyan-300" data-testid="rank-letter">{p.letter}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="mono-label">{c.class_primary}{c.class_secondary ? ` / ${c.class_secondary}` : ""}</p>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-1">{c.name}</h1>
              <p className="text-cyan-300 font-display text-lg mt-1">{c.build_name}</p>
              <p className="text-slate-400 text-sm mt-2 max-w-2xl">{c.build_desc}</p>
            </div>
            <div className="flex md:flex-col gap-4 md:text-right">
              <div><p className="mono-label">LEVEL</p><p className="font-display text-3xl font-bold" data-testid="level-value">{p.level}</p></div>
              <div><p className="mono-label">RANK</p><p className="font-display text-xl font-semibold text-cyan-300">{p.rank}</p></div>
              <div><p className="mono-label">SKILL PTS</p><p className="font-display text-3xl font-bold text-amber-300" data-testid="skill-points">{p.skill_points}</p></div>
            </div>
          </div>
          {/* XP bar */}
          <div className="mt-6">
            <div className="flex justify-between mono-label mb-2">
              <span>{p.xp} XP</span>
              <span>{p.rank_next ? `NEXT: ${p.rank_next} XP` : "MAX RANK"}</span>
            </div>
            <div className="h-3 rounded-full bg-slate-800/60 overflow-hidden border border-blue-900/40">
              <motion.div initial={{ width: 0 }} animate={{ width: `${rankPct}%` }} transition={{ duration: 0.8 }}
                className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 shadow-[0_0_16px_rgba(0,240,255,0.6)]" data-testid="xp-bar" />
            </div>
          </div>
          {c.win_condition && (
            <div className="mt-5 flex items-center gap-2 text-sm text-slate-300 border border-blue-900/30 rounded-lg px-4 py-3">
              <Target size={16} className="text-cyan-400 shrink-0" />
              <span><span className="mono-label mr-2">WIN CONDITION</span>{c.win_condition}</span>
            </div>
          )}
        </motion.div>

        {/* Stats */}
        <section data-testid="stats-section">
          <SectionTitle icon={Shield} label="CORE STATS" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {STAT_ORDER.map((k, i) => {
              const val = c.stats?.[k] ?? 10;
              return (
                <motion.div key={k} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="glass rounded-xl p-4 text-center" data-testid={`stat-${k}`}>
                  <p className="mono-label">{STAT_META[k].label}</p>
                  <p className="font-display text-3xl font-bold text-cyan-300 mt-1">{val}</p>
                  <div className="h-1.5 mt-3 rounded-full bg-slate-800 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400" style={{ width: `${(val / 20) * 100}%` }} />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Skills */}
        <div className="grid md:grid-cols-2 gap-6">
          <SkillList title="ACTIVE SKILLS" icon={Zap} color="text-cyan-400" items={c.skills_active} testid="active-skills" />
          <SkillList title="PASSIVES" icon={Sparkles} color="text-blue-400" items={c.skills_passive} testid="passive-skills" />
        </div>

        {/* Weaknesses */}
        <section data-testid="weakness-section">
          <SectionTitle icon={AlertTriangle} label="WEAKNESSES / NERFS" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(c.weaknesses || []).map((w, i) => (
              <div key={i} className="rounded-xl p-4 border border-red-500/30 bg-red-500/5" data-testid={`weakness-${i}`}>
                <p className="font-display font-semibold text-red-300">{w.name}</p>
                <p className="text-slate-400 text-sm mt-1">{w.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Skill tree */}
        <section data-testid="skill-tree-section">
          <SectionTitle icon={Swords} label="SKILL TREE" hint={`${p.skill_points} points available — level up to earn more`} />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(c.skill_tree || []).map((tier, ti) => (
              <div key={ti} className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="mono-label">TIER {tier.tier + 1}</span>
                  <span className="text-xs text-slate-500 truncate">{tier.title}</span>
                </div>
                {tier.nodes.map((n, ni) => {
                  const unlocked = nodeUnlocked(ti, ni, n.base);
                  return (
                    <div key={ni} data-testid={`node-${ti}-${ni}`}
                      className={`rounded-xl p-4 border transition-all ${unlocked ? "border-cyan-400/40 bg-cyan-400/5" : "border-slate-800 bg-slate-900/40 opacity-80"}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className={`font-display font-semibold ${unlocked ? "text-cyan-200" : "text-slate-400"}`}>{n.name}</p>
                          <span className="mono-label">{n.type}</span>
                        </div>
                        {unlocked
                          ? <Check size={16} className="text-cyan-400 shrink-0" />
                          : <Lock size={15} className="text-slate-600 shrink-0" />}
                      </div>
                      <p className="text-slate-400 text-xs mt-2 leading-relaxed">{n.desc}</p>
                      {!unlocked && (
                        <button data-testid={`unlock-${ti}-${ni}`} onClick={() => unlockNode(ti, ni)} disabled={p.skill_points <= 0}
                          className="mt-3 w-full text-xs font-medium flex items-center justify-center gap-1 border border-cyan-400/40 text-cyan-300 rounded-lg py-1.5 hover:bg-cyan-400/10 disabled:opacity-40 transition-all">
                          <Zap size={12} /> Unlock (1 pt)
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </section>

        {/* Daily training */}
        <section className="pb-10" data-testid="daily-section">
          <SectionTitle icon={Flame} label="DAILY TRAINING — EARN XP" hint="Resets each day" />
          <div className="space-y-3">
            {(c.daily_plan || []).map((t, i) => {
              const done = data.today_done.includes(i);
              return (
                <div key={i} className={`glass rounded-xl p-4 flex items-center gap-4 ${done ? "opacity-60" : ""}`} data-testid={`daily-${i}`}>
                  <button data-testid={`log-${i}`} onClick={() => logTask(i)} disabled={done}
                    className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 border transition-all ${done ? "bg-cyan-400 border-cyan-400 text-black" : "border-blue-700 hover:bg-blue-500/10"}`}>
                    {done ? <Check size={18} /> : <ChevronRight size={18} className="text-cyan-300" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`font-display font-semibold ${done ? "line-through text-slate-500" : "text-slate-100"}`}>{t.title}</p>
                      <span className="mono-label text-amber-300">+{t.xp} XP</span>
                    </div>
                    <p className="text-slate-400 text-sm mt-0.5">{t.detail}</p>
                  </div>
                  <span className="mono-label hidden sm:block">{t.category}</span>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, label, hint }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <Icon className="text-cyan-400" size={18} />
      <h2 className="font-display text-lg font-semibold tracking-wide">{label}</h2>
      {hint && <span className="text-xs text-slate-500 ml-auto">{hint}</span>}
    </div>
  );
}

function SkillList({ title, icon: Icon, color, items, testid }) {
  return (
    <section data-testid={testid}>
      <SectionTitle icon={Icon} label={title} />
      <div className="space-y-3">
        {(items || []).map((s, i) => (
          <div key={i} className="glass rounded-xl p-4" data-testid={`${testid}-item-${i}`}>
            <p className={`font-display font-semibold ${color}`}>{s.name}</p>
            <p className="text-slate-400 text-sm mt-1 leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
